import React, { useMemo, useContext } from 'react';
import styled from '@emotion/styled';
import { DataGrid } from '@mui/x-data-grid';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import type { Theme } from '@trinity/chat-components/types/theme';
import { getThemeValue, getThemeColor, THEME } from './DynamicForm.styles.theme';

type StyledComponentProps = {
  theme?: Theme.Theme;
};

// =============================================================================
// BUTTON COMPONENTS
// =============================================================================

export const Root = styled.button`
  display: block;
`;

export const Wrap = styled('div', {
  shouldForwardProp: prop => prop !== 'variant' && prop !== 'isDisabled',
}) <{ variant?: 'primary' | 'secondary' | 'tertiary', isDisabled?: boolean }>`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => props.theme.palette.text.primary};
  cursor: ${props => props.isDisabled ? 'default' : 'pointer'};
  border-radius: 2px;
  font-size: ${THEME.fontSize.sm};
  font-family: ${props => props.theme.fonts.regular};
  padding: 6px 8px;
  transition: background-color 0.2s;
  text-align: left;
`;

type ButtonColorConfig = {
  enabled: {
    backgroundColor: string;
    hover: {
      backgroundColor: string;
    };
  };
  disabled: {
    backgroundColor: string;
    hover: {
      backgroundColor: string;
    };
  };
};

type ButtonColors = {
  primary: ButtonColorConfig;
  secondary: ButtonColorConfig;
  tertiary: ButtonColorConfig;
  text: ButtonColorConfig;
};

export const getButtonColors = (theme: Theme.Theme | undefined): ButtonColors => {
  const { gray } = theme?.palette || {};
  const borderLightFallback = gray?.light2 || getThemeColor.borderLight(theme);

  return {
    primary: {
      enabled: {
        backgroundColor: getThemeColor.primary(theme),
        hover: {
          backgroundColor: getThemeColor.primaryHover(theme),
        },
      },
      disabled: {
        backgroundColor: borderLightFallback,
        hover: {
          backgroundColor: borderLightFallback,
        },
      },
    },
    secondary: {
      enabled: {
        backgroundColor: getThemeColor.secondary(theme),
        hover: {
          backgroundColor: getThemeColor.secondaryHover(theme),
        },
      },
      disabled: {
        backgroundColor: borderLightFallback,
        hover: {
          backgroundColor: borderLightFallback,
        },
      },
    },
    tertiary: {
      enabled: {
        backgroundColor: getThemeColor.tertiary(theme),
        hover: {
          backgroundColor: getThemeColor.tertiaryHover(theme),
        },
      },
      disabled: {
        backgroundColor: borderLightFallback,
        hover: {
          backgroundColor: borderLightFallback,
        },
      },
    },
    text: {
      enabled: {
        backgroundColor: 'transparent',
        hover: {
          backgroundColor: borderLightFallback,
        },
      },
      disabled: {
        backgroundColor: 'transparent',
        hover: {
          backgroundColor: 'transparent',
        },
      },
    },
  };
};

type ActionButtonProps = {
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary';
  icon?: React.ElementType;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'data-testid'?: string;
  type?: 'button' | 'submit' | 'reset';
};

export const ActionButton: React.FC<ActionButtonProps> = React.memo(({
  onClick,
  children,
  variant = 'primary',
  icon: Icon,
  disabled = false,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
  type = 'button',
}) => {
  const { palette, theme: wl } = useContext(AppThemingContext);

  const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled) return;
    if (type === 'submit') return;
    try {
      onClick();
    } catch (error) {
      // Error handled silently in production
    }
  }, [onClick, disabled, type]);

  // Determine icon color based on whitelabeling and variant
  // Use palette values directly from context when available, otherwise use theme color helpers
  const getIconColor = () => {
    if (wl?.enabled && variant === 'primary' && palette?.prompt?.icon) {
      return palette.prompt.icon;
    }
    // Use palette values directly from context (WhiteLabelPalette has green but not black/purple)
    if (palette) {
      if (variant === 'tertiary') {
        return palette.green?.dark2 || getThemeColor.iconColorTertiary(undefined);
      }
    }
    // Fallback to theme color helpers
    return variant === 'tertiary'
      ? getThemeColor.iconColorTertiary(undefined)
      : variant === 'secondary'
        ? getThemeColor.iconColorSecondary(undefined)
        : getThemeColor.iconColor(undefined);
  };
  const iconColor = getIconColor();

  return (
    <Root
      className={className}
      onClick={type === 'submit' ? undefined : (disabled ? undefined : handleClick)}
      type={type}
    >
      <ActionButtonWrap
        variant={variant}
        isDisabled={disabled}
        wlEnabled={wl?.enabled}
        palette={palette}
      >
        {Icon && (
          <Icon
            style={{
              height: '16px',
              width: '16px',
              fontSize: '18px',
              marginRight: '4px',
              color: iconColor,
            }}
            aria-hidden="true"
          />
        )}
        {children}
      </ActionButtonWrap>
    </Root>
  );
});

ActionButton.displayName = 'ActionButton';

const ActionButtonWrap = styled(Wrap)<{
  variant?: 'primary' | 'secondary' | 'tertiary' | 'text';
  isDisabled?: boolean;
  wlEnabled?: boolean;
  palette?: Theme.WhiteLabelPalette;
}>(({ theme, variant = 'secondary', isDisabled, wlEnabled, palette }) => {
  const colorStyles = useMemo(() => getButtonColors(theme), [theme]);
  const currentVariant = colorStyles[variant];
  const currentValues = isDisabled ? currentVariant.disabled : currentVariant.enabled;
  // Use whitelabel colors for primary buttons when enabled
  const backgroundColor = wlEnabled && variant === 'primary' && palette?.prompt?.main
    ? palette.prompt.main
    : currentValues.backgroundColor;

  const hoverBackgroundColor = wlEnabled && variant === 'primary' && palette?.prompt?.dark1
    ? palette.prompt.dark1
    : currentValues.hover.backgroundColor;

  // Set explicit text color for better visibility, especially for tertiary buttons
  const textColor = variant === 'tertiary'
    ? getThemeColor.text(theme)
    : variant === 'primary'
      ? getThemeColor.text(theme)
      : variant === 'secondary'
        ? getThemeColor.text(theme)
        : 'inherit';

  return {
    backgroundColor,
    color: textColor,
    '&:hover': {
      backgroundColor: hoverBackgroundColor,
    },
  };
});

// =============================================================================
// DRAFT TABLE COMPONENTS
// =============================================================================

export const DraftTableContainer = styled.div<StyledComponentProps>(({ theme }) => ({
  marginTop: THEME.spacing.lg,
  width: '100%',
}));

export const StyledDataGrid = styled(DataGrid)<StyledComponentProps>(({ theme }) => {
  const grayLight3 = getThemeValue(
    (t) => t.palette?.gray?.light3,
    getThemeColor.backgroundLight(theme),
    theme,
  );
  const grayLight4 = getThemeColor.border(theme);
  const white = getThemeColor.background(theme);
  const textPrimary = getThemeColor.text(theme);

  return {
    '& .MuiDataGrid-cell': {
      whiteSpace: 'normal !important' as const,
      wordBreak: 'break-word',
      padding: THEME.spacing.sm,
      lineHeight: 1.4,
      color: textPrimary,
    },
    '& .MuiDataGrid-columnHeader': {
      fontWeight: 'bold !important',
      backgroundColor: grayLight3,
      color: textPrimary,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold !important',
    },
    '& .MuiDataGrid-row.odd': {
      backgroundColor: grayLight3,
      '&:hover': {
        backgroundColor: grayLight3,
      },
    },
    '& .MuiDataGrid-row.even': {
      backgroundColor: white,
      '&:hover': {
        backgroundColor: grayLight3,
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none !important',
    },
    '& .MuiDataGrid-iconSeparator': {
      display: 'none !important',
    },
  };
});

export const ACCESSIBILITY_CONSTANTS = {
  FOCUS_RING_WIDTH: '3px',
  FOCUS_RING_OPACITY: '0.25',
  MIN_TOUCH_TARGET: '44px',
  ANIMATION_DURATION: {
    FAST: '0.15s',
    MEDIUM: '0.2s',
    SLOW: '0.3s',
  },
} as const;

